/**
 * Compliance-mode identity for the admin panel.
 *
 * WHY THIS EXISTS
 * The same reason `chains.ts` exists: the identity gate an asset uses now decides several
 * presentation questions — what to call it in a picker, how to describe the trade-off to
 * whoever is choosing, and what a mismatch between requested and enforced means — and each
 * would otherwise be answered by a literal pasted into whichever component needed it.
 *
 * WHAT THIS IS NOT
 * Not a source of truth. The backend decides which modes a given chain can actually issue
 * under: ACE needs a PolicyEngine deployed on that specific network, the legacy gate needs a
 * claim issuer and a KYC workflow there. Offering a mode here that a chain cannot provide is
 * expected and safe — creation is rejected server-side with a message naming the missing
 * setting, exactly as an unsupported chain already is. Filtering client-side instead would
 * mean duplicating the backend's capability rules and drifting from them.
 */

export type ComplianceMode = 'ACE_ONLY' | 'DUAL' | 'LEGACY';

export interface CompliancePresentation {
  id: ComplianceMode;
  label: string;
  /** One line for the picker, aimed at whoever is choosing. */
  description: string;
  /** Whether an asset can be created under this mode from the panel. */
  selectable: boolean;
}

export const COMPLIANCE_MODES: Record<ComplianceMode, CompliancePresentation> =
  {
    ACE_ONLY: {
      id: 'ACE_ONLY',
      label: 'ACE (Chainlink)',
      description:
        'One credential per investor, valid for every asset. Requires ACE on the selected chain.',
      selectable: true,
    },
    LEGACY: {
      id: 'LEGACY',
      label: 'ERC-3643 registry',
      description:
        'Per-asset identity registry. Investors must be registered for this asset specifically.',
      selectable: true,
    },
    DUAL: {
      id: 'DUAL',
      label: 'Both (ACE + registry)',
      description:
        'Both gates must pass. Requires the chain to support ACE and the legacy stack.',
      selectable: true,
    },
  };

/**
 * The modes an asset can be created under, in the order they should appear in a picker.
 *
 * ACE first because it is the default and what every existing asset uses.
 */
export const SELECTABLE_COMPLIANCE_MODES = (
  ['ACE_ONLY', 'LEGACY', 'DUAL'] as ComplianceMode[]
)
  .map((id) => COMPLIANCE_MODES[id])
  .filter((mode) => mode.selectable);

/** Display name for a mode. Falls back to the raw value, never to blank. */
export function complianceLabel(mode: string | null | undefined): string {
  if (!mode) return 'N/A';
  return COMPLIANCE_MODES[mode as ComplianceMode]?.label ?? mode;
}

/**
 * How an asset's compliance is actually doing, from the two modes the API returns.
 *
 * `engine` is what the deployed token ENFORCES; `requestedEngine` is what was ASKED FOR.
 * They disagree legitimately while an asset provisions and permanently when provisioning
 * failed — and the failed case is not cosmetic. A token requested as ACE_ONLY whose
 * PolicyEngine never attached reverts on every transfer and mint, so this is the difference
 * between an asset that works and one that silently cannot trade.
 */
export type ComplianceHealth = 'OK' | 'PROVISIONING' | 'BROKEN' | 'UNKNOWN';

export function complianceHealth(
  compliance?: {
    engine?: string | null;
    requestedEngine?: string | null;
    inSync?: boolean | null;
    aceTargetStatus?: string | null;
  } | null,
): ComplianceHealth {
  if (!compliance?.engine || !compliance?.requestedEngine) return 'UNKNOWN';
  if (compliance.inSync) return 'OK';
  // An ACE target still coming up on-chain is the normal intermediate state, not a fault.
  if (compliance.aceTargetStatus === 'PENDING') return 'PROVISIONING';
  return 'BROKEN';
}

/** Short, human explanation of a health state, safe to show in a tooltip or badge. */
export function complianceHealthLabel(health: ComplianceHealth): string {
  switch (health) {
    case 'OK':
      return 'Enforced';
    case 'PROVISIONING':
      return 'Provisioning';
    case 'BROKEN':
      return 'Not enforced — cannot trade';
    default:
      return 'Unknown';
  }
}
